// src/components/landingpage/pp-terminalx/TerminalXPage.jsx
//
// The TerminalX product page, built from the "terminalx-app-page" screen of
// the Kuestion Web mockup (enhancements log, 18 Oct 2026, second entry):
// hero and principles, the Helper, the safety poster with its command chips
// and diagram, the six-feature carousel with thumbnails (TFTP server opens
// first), Day one with the real first-start screenshot, keyboard shortcuts,
// the comparison table, install and specs, and the FAQ accordion.
//
// Every screenshot opens the full-screen viewer (Lightbox), which walks all
// ten images in gallery order. Copy is the product's about.md and media-kit
// generator as carried by the design; images are the media kit at 2x,
// cropped to the app window, so every word on the page is real text.
//
// The page opens in the app's Midnight theme and the navbar toggle switches
// it to Daylight: that is useAppTheme in MasterPage, not anything here.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TERMINALX_RELEASE } from '../../../config/terminalx';
import { DOWNLOADS_PATH, DOWNLOAD_APPS } from '../../../config/downloads';
import { loadDownload } from '../downloads/downloadsFeed';
import Lightbox from './Lightbox';
import appIcon from '../../../assets/images/terminalx/app.svg';
import shot00 from '../../../assets/images/terminalx/shot-00.png';
import shot01 from '../../../assets/images/terminalx/shot-01.png';
import shot02 from '../../../assets/images/terminalx/shot-02.png';
import shot03 from '../../../assets/images/terminalx/shot-03.png';
import shot04 from '../../../assets/images/terminalx/shot-04.png';
import shot05 from '../../../assets/images/terminalx/shot-05.png';
import shot06 from '../../../assets/images/terminalx/shot-06.png';
import shot07 from '../../../assets/images/terminalx/shot-07.png';
import shot08 from '../../../assets/images/terminalx/shot-08.png';
import shot09 from '../../../assets/images/terminalx/shot-09.png';
import './TerminalXPage.css';

// The Downloads page's entry for this app: its manifest on GitHub gives the
// live version and download size. One read per page load, shared with the
// Downloads page; any failure leaves the figures written in config.
const TX_APP = DOWNLOAD_APPS.find((a) => a.id === 'terminalx-windows');

// The viewer's order. `ratio` is the image's own size, so the frame holds
// its shape before the file arrives.
export const GALLERY = [
  { key: 'hero', src: shot01, ratio: '3720 / 2060', cap: 'The whole window: rail, hosts, console and the Helper' },
  { key: 'helper', src: shot02, ratio: '2440 / 1800', cap: 'The Helper drafts the change; the review dialog shows every line before it is typed' },
  { key: 'safety', src: shot03, ratio: '2400 / 1490', cap: 'The safety model: the disruptive-command list and the four gates' },
  { key: 'terminal', src: shot04, ratio: '2440 / 1760', cap: 'The terminal: paging answered for you, console colouring, 7-bit safe paste' },
  { key: 'hosts', src: shot05, ratio: '2192 / 1634', cap: 'Hosts in folders and the host editor with serial, telnet, SSH and local shell settings' },
  { key: 'scanner', src: shot06, ratio: '2440 / 1720', cap: 'The IP scanner: live hosts with name, MAC, maker, open ports, banners and a device guess' },
  { key: 'tftp', src: shot07, ratio: '2440 / 1760', cap: 'The TFTP server: files, the copy line for the current device, and live transfers' },
  { key: 'api', src: shot09, ratio: '2440 / 1760', cap: 'Device API requests in the Helper, and the per-device memory file' },
  { key: 'themes', src: shot08, ratio: '2376 / 1136', cap: 'The eight themes' },
  { key: 'first', src: shot00, ratio: '2220 / 1350', cap: 'A real screenshot of TerminalX 0.2.0 on first start' },
];
const shot = (key) => GALLERY.find((g) => g.key === key);

const PRINCIPLES = [
  ['One window', 'Console, documentation lookup and assistant together'],
  ['Your own devices', 'No server, no agent, no change to the network'],
  ['Your own AI account', 'Bring-your-own Anthropic account, no shared credentials'],
  ['No telemetry', 'Nothing leaves the building that you did not choose to send'],
];

const HELPER = [
  ['01', 'It answers', 'When the answer is on the screen it reads it and tells you, with the mechanism explained and the exact syntax included.'],
  ['02', 'It looks', 'Show commands run at once, read-only, no prompt. The output goes back to the Helper and it carries on.'],
  ['03', 'It changes', 'Configuration waits for your approval, line by line. Lines are paced against the device and stop on the first error.'],
  ['04', 'It verifies and reports', 'Verification runs afterwards and the Helper reports the result. A rejected line is handed back, corrected and continued.'],
];

const SAFETY = [
  ['The Helper returns text only', 'It never holds the connection. TerminalX parses only tagged verify, apply and api blocks; everything else is illustration.'],
  ['Destructive commands wait for a typed yes', 'A fixed list in the program, not in any prompt. It applies even in Auto mode, whatever the model thinks.'],
  ['Four more gates', 'Show-only verify, no placeholders, stop on the first error, and round caps so a request cannot loop.'],
  ['A sandboxed engine', 'File tools confined to one Helper folder, web search and read-only Drive. No shell. No sockets. No serial port.'],
];

const RULES = ['reload', 'write erase', 'erase startup-config', 'format', 'delete /force', 'switch N renumber', 'no interface', 'no vlan', 'shutdown', 'no ip address', 'boot system', 'redundancy', 'hw-module', 'license', 'no switchport', 'channel-group'];

export const SLIDES = [
  { id: 'terminal', label: 'Terminal', kicker: '01 · A real terminal', title: 'A terminal built for device consoles.',
    body: 'Keys go straight to the device. Tab, ?, Ctrl-C, Ctrl-Z, Ctrl-Shift-6 and paging behave exactly as on a physical console.',
    points: ['--More-- answered for you; paging switched off on twelve platforms in one click.', 'Prompts, errors, up/down, addresses and interfaces tinted from the theme.', '7-bit safe paste: dashes and curly quotes fixed before they break a line.', 'PuTTY-style select-to-copy, right-click paste, scrollback bar, Ctrl+wheel zoom.'] },
  { id: 'hosts', label: 'Connections & hosts', kicker: '02 · Connections & hosts', title: 'Serial, telnet, SSH and a shell. All your hosts, in folders.',
    body: 'Termius-style host management with the details a device console actually needs, saved per host.',
    points: ['Folders, colour tags, search with Ctrl+K, double-click to connect.', 'Per-host Enter and Backspace codes, extra SSH options, serial line control.', 'Passwords saved only when you ask, sealed with Windows DPAPI.', 'Helper access per host: console, API or both.'] },
  { id: 'scanner', label: 'IP scanner', kicker: '03 · IP scanner', title: 'Find every device on the segment, firewalled or not.',
    body: 'Ping, ARP, a TCP knock and a UDP probe, the way Angry IP Scanner does it. Then name, MAC, maker, ports, banners and a guess at what it is.',
    points: ['Reverse DNS, MAC vendor, SSH / TLS / HTTP banners, device guess.', 'Double-click a row for a console; right-click to SSH, telnet, open the web UI or save as a host.', 'Live-only filter, sortable columns, Copy table, Export CSV.', 'No administrator rights needed.'] },
  { id: 'tftp', label: 'TFTP server', kicker: '04 · TFTP server', title: 'Serve an image, back up a config, without leaving the console.',
    body: 'Drop files on the tab, press Start, and the exact copy command for the device in front of you is ready, with your address already filled in.',
    points: ['Copy lines for twelve platforms, loading and backing up.', 'Live transfers with progress and speed; 8 KB blocks, files past 32 MB.', 'Folder-jailed; uploads from devices only when you allow them.', 'Firewall rule offered when Windows has none; the Helper knows what you dropped in.'] },
  { id: 'api', label: 'Device API & memory', kicker: '05 · Device API & memory', title: 'Talks REST when the device has one. Remembers what it learned.',
    body: 'RESTCONF, NX-API, PAN-OS, Meraki, AOS-CX, FortiOS and friends. Writes go through the same approval as config lines; credentials are added by TerminalX, never seen by the model.',
    points: ['GET runs at once; POST, PUT, PATCH, DELETE wait for approval.', 'Stages and commits separately on PAN-OS and Junos; verifies with a read-back.', 'Per-device notes: model, version, addressing, every change and why.', 'Optional Memora memory server, one store per Helper folder.'] },
  { id: 'themes', label: 'Themes', kicker: '06 · Look & feel', title: 'Eight themes. Everything restyles live, terminal included.',
    body: 'Native Qt widgets, a wallpaper top bar in the theme\'s own colours, a tool rail, sliding panels and a connection light on every tab.',
    points: ['Midnight, Nord, Dracula, Solarized Dark, One Dark, Gruvbox Dark, Catppuccin Mocha and Daylight.', 'Terminal font, size, cursor style and breathing cursor of your choice.', 'Console colouring follows the palette; the device\'s own colours always win.'] },
];

// The design opens on the TFTP server slide.
export const FIRST_SLIDE = 3;

const KEYS = [
  [['Ctrl', 'K'], 'Open the host panel and jump to search'],
  [['Ctrl', 'J'], 'Show or hide the Helper'],
  [['Ctrl', 'B'], 'Show or hide the host panel'],
  [['Ctrl', 'Shift', 'T'], 'New tab'],
  [['Ctrl', 'Shift', 'D'], 'Connect or disconnect the current tab'],
  [['Ctrl', 'Shift', 'L'], 'Logging on or off for the current tab'],
  [['Ctrl', 'Shift', 'I'], 'IP scanner'],
  [['Ctrl', 'Shift', 'U'], 'TFTP server'],
  [['Ctrl', 'Shift', 'Q'], 'Lock'],
];

const COMPARE = [
  ['Sees the live console', 'Yes', 'No', 'Yes'],
  ['Answers questions about what is on screen', 'No', 'Only if you paste it', 'Yes, automatically'],
  ['Runs show commands to find out', 'No', 'No', 'Yes, read-only, no approval needed'],
  ['Drafts configuration', 'No', 'Yes, unverified', 'Yes, then types it after your approval'],
  ['Stops on device errors and recovers', 'No', 'No', 'Yes, hands the error back and continues'],
  ['Hard stop on destructive commands', 'No', 'No', 'Yes, enforced in code, not by the model'],
  ['Remembers each device', 'No', 'No', 'Yes, per-device notes or a memory server'],
  ['IP scanner, TFTP server built in', 'Rarely', 'No', 'Yes'],
];

const SPECS = [
  ['Operating system', TERMINALX_RELEASE.requirements],
  ['SSH', 'The OpenSSH client included with Windows'],
  ['AI Helper', 'An Anthropic account for Claude Code (installed by the setup if missing)'],
  ['Serial', 'Any COM port, USB-to-serial adapters included'],
  ['Disk', 'About 60 MB installed'],
  ['Built with', 'C++20, Qt 6 Widgets'],
  ['Licence', 'Commercial, under an end user licence agreement'],
];

export const FAQ = [
  ['Does the Helper type into my devices without asking?', 'Only when you switch on Auto, and even then disruptive commands stop for a typed yes. With Auto off, every configuration block goes through the review dialog. Show commands run without asking because they change nothing.'],
  ['What does the AI see?', 'The recent console output (a few hundred lines), your message, any file you attach, and the notes it keeps for that device. Device credentials are never shown to it; TerminalX adds them to API calls itself.'],
  ['Can it reload a switch by accident?', 'No. reload, write erase, format, delete /force, shutdown, no ip address, boot system and the rest of the list always stop for your explicit confirmation. The list is in the program, not in a prompt the model could talk itself around.'],
  ['Do I need an AI subscription?', 'The Helper uses your own Anthropic account through Claude Code. Everything else in TerminalX, the consoles, the scanner, the TFTP server, works without one.'],
  ['Does it work offline?', 'The consoles, host management, scanner and TFTP server all work offline. The Helper needs a connection to reach the model.'],
  ['Which vendors are supported?', 'Any device with a serial, telnet or SSH console. Paging, TFTP commands and API styles have specific support for Cisco, Arista, Juniper, HPE, Aruba, Fortinet, Palo Alto, MikroTik, Huawei and Meraki. The Helper looks up what it does not already know.'],
];

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 4v11" /><polyline points="7 10 12 15 17 10" /><path d="M4 19h16" />
  </svg>
);
const Tick = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="3" strokeLinecap="square" aria-hidden="true">
    <polyline points="4 12 10 18 20 6" />
  </svg>
);
const Chevron = ({ dir }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="square" aria-hidden="true">
    <polyline points={dir < 0 ? '15 5 8 12 15 19' : '9 5 16 12 9 19'} />
  </svg>
);

const DownloadButton = ({ children }) => (
  <a href={TERMINALX_RELEASE.installerUrl} download={TERMINALX_RELEASE.file} className="rw-btn rw-btn--primary rw-tx__btn">
    <DownloadIcon />
    {children}
  </a>
);

/** A screenshot that opens the viewer. `alt` is the page's own caption for it. */
const Shot = ({ item, alt, onZoom, eager = false, onPoster = false }) => (
  <button type="button" onClick={() => onZoom(item.key)} aria-label={`Zoom: ${alt}`} className={'rw-tx__shot' + (onPoster ? ' rw-tx__shot--poster' : '')}>
    <img src={item.src} alt={alt} loading={eager ? undefined : 'lazy'} style={{ aspectRatio: item.ratio }} />
    <span aria-hidden="true" className="rw-tx__shot-badge">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square">
        <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16" y2="16" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
      </svg>
      Click to zoom
    </span>
  </button>
);

export default function TerminalXPage() {
  const [idx, setIdx] = useState(FIRST_SLIDE);
  const [lb, setLb] = useState(null);           // index into GALLERY, or null
  const [faqOpen, setFaqOpen] = useState([0]);
  const touchX = useRef(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    let on = true;
    if (TX_APP) loadDownload(TX_APP).then((d) => { if (on) setLive(d); });
    return () => { on = false; };
  }, []);
  const r = TERMINALX_RELEASE;
  const version = (live && live.version) || r.version;
  const downloadSize = live ? ((live.details.find((d) => d.key === 'size') || {}).value || '') : '';
  const sizes = (downloadSize ? `${downloadSize} download · ` : '') + `${r.installedSize} installed`;
  const n = SLIDES.length;
  const go = (i) => setIdx((i + n) % n);
  const zoom = (key) => setLb(GALLERY.findIndex((g) => g.key === key));
  const pad = (i) => String(i).padStart(2, '0');

  const onTouchStart = (e) => { touchX.current = e.touches && e.touches[0] ? e.touches[0].clientX : null; };
  const onTouchEnd = (e) => {
    const x = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null;
    if (touchX.current == null || x == null) return;
    const dx = x - touchX.current;
    touchX.current = null;
    if (dx < -50) go(idx + 1); else if (dx > 50) go(idx - 1);
  };
  const toggleFaq = (i) => setFaqOpen((o) => (o.includes(i) ? o.filter((x) => x !== i) : o.concat(i)));

  return (
    <main className="rw-tx">
      {/* ── hero ─────────────────────────────────────────────────── */}
      <section className="rw-tx__hero">
        <div className="rw-tx__hero-brand">
          <img src={appIcon} alt="" width="56" height="56" />
          <span className="rw-kicker">Application · Windows</span>
        </div>
        <div className="rw-tx__hero-grid">
          <div>
            <h1 className="rw-tx__h1">TerminalX</h1>
            <p className="rw-tx__tagline">The console client with an engineer's assistant built in.</p>
          </div>
          <div className="rw-tx__hero-side">
            <p className="rw-tx__lead">
              Serial, telnet and SSH consoles in one fast, native Windows window, with an AI Helper
              beside you that reads the same screen you do. Ask it a question and it answers from the
              console. Ask it to check something and it runs the <code>show</code> commands itself.
              Ask for a change and it drafts the exact configuration, waits for your approval, types
              it line by line and verifies the result.
            </p>
            <div className="rw-tx__actions">
              <DownloadButton>Download for Windows</DownloadButton>
              <a href="#tx-tour" className="rw-btn rw-btn--secondary rw-tx__btn">Take the tour</a>
            </div>
            <div className="rw-tx__meta">Version {version} · {r.requirements} · {sizes} · no administrator prompt</div>
          </div>
        </div>
      </section>

      <section className="rw-tx__shot-section">
        <Shot item={shot('hero')} alt="TerminalX: hosts, console and the Helper in one window" onZoom={zoom} eager />
        <div className="rw-tx__cells rw-tx__cells--principles">
          {PRINCIPLES.map(([title, body]) => (
            <div key={title} className="rw-tx__cell">
              <div className="rw-tx__cell-title">{title}</div>
              <div className="rw-tx__cell-body">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── the Helper ───────────────────────────────────────────── */}
      <section className="rw-tx__section rw-tx__section--rule">
        <div className="rw-tx__inner">
          <div className="rw-tx__intro">
            <div>
              <div className="rw-kicker">The Helper</div>
              <h2 className="rw-tx__h2 rw-tx__h2--big rw-tx__h2--helper">Drafted by the Helper. Approved by you. Typed by TerminalX.</h2>
            </div>
            <p className="rw-tx__p">
              Say what you want in plain language. The Helper reads the console, drafts the exact
              commands and shows you every line before anything is typed. It runs on Claude through
              Claude Code, signed in with your own Anthropic account.
            </p>
          </div>
          <div className="rw-tx__helper">
            <div className="rw-tx__helper-list">
              {HELPER.map(([num, title, body]) => (
                <div key={num} className="rw-tx__helper-row">
                  <span className="rw-tx__helper-num">{num}</span>
                  <span className="rw-tx__helper-text">
                    <span className="rw-tx__helper-title">{title}</span>
                    <span className="rw-tx__helper-body">{body}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="rw-tx__helper-shot">
              <Shot item={shot('helper')} alt="A drafted change in the review dialog, every line visible before it is typed" onZoom={zoom} />
            </div>
          </div>
        </div>
      </section>

      {/* ── safety poster ────────────────────────────────────────── */}
      <section id="tx-safety" className="rw-tx__poster">
        <div className="rw-tx__inner rw-tx__poster-inner">
          <div className="rw-tx__poster-grid">
            <div>
              <div className="rw-tx__poster-kicker">Safety model</div>
              <h2 className="rw-tx__poster-h2">The program holds the pen. The model only suggests.</h2>
              <p className="rw-tx__poster-sub">TerminalX was designed for production equipment. Every rule that protects the device lives in the application, where no prompt can talk its way around it.</p>
            </div>
            <div className="rw-tx__rules">
              {SAFETY.map(([title, body]) => (
                <div key={title} className="rw-tx__rule">
                  <div className="rw-tx__rule-title">{title}</div>
                  <div className="rw-tx__rule-body">{body}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rw-tx__chips">
            {RULES.map((c) => <span key={c} className="rw-tx__chip">{c}</span>)}
          </div>
          <Shot item={shot('safety')} alt="The safety model in the app: the disruptive-command list and the four gates" onZoom={zoom} onPoster />
        </div>
      </section>

      {/* ── inside the window: carousel ──────────────────────────── */}
      <section id="tx-tour" className="rw-tx__section rw-tx__section--bottom">
        <div className="rw-tx__inner">
          <div className="rw-tx__tour-head">
            <div className="rw-tx__tour-title">
              <div className="rw-kicker">Inside the window</div>
              <h2 className="rw-tx__h2 rw-tx__h2--big rw-tx__h2--tabs">Everything a console day needs, built in.</h2>
            </div>
            <div className="rw-tx__tour-nav">
              <span className="rw-tx__counter" aria-live="polite">{pad(idx + 1)} / {pad(n)}</span>
              <button type="button" onClick={() => go(idx - 1)} aria-label="Previous feature" className="rw-tx__arrow"><Chevron dir={-1} /></button>
              <button type="button" onClick={() => go(idx + 1)} aria-label="Next feature" className="rw-tx__arrow"><Chevron dir={1} /></button>
            </div>
          </div>
          <div className="rw-tx__progress"><div className="rw-tx__progress-bar" style={{ width: `${((idx + 1) / n) * 100}%` }} /></div>
          <div aria-roledescription="carousel" aria-label="TerminalX features" className="rw-tx__carousel" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="rw-tx__track" data-testid="tx-track" style={{ transform: `translateX(${-idx * 100}%)` }}>
              {SLIDES.map((s, i) => (
                <div key={s.id} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${n}: ${s.label}`} aria-hidden={i !== idx} className="rw-tx__slide">
                  <div className="rw-tx__slide-shot">
                    <Shot item={shot(s.id)} alt={s.title} onZoom={zoom} />
                  </div>
                  <div className="rw-tx__slide-text">
                    <span className="rw-kicker">{s.kicker}</span>
                    <h3 className="rw-tx__h3">{s.title}</h3>
                    <p className="rw-tx__p rw-tx__p--tight">{s.body}</p>
                    <div className="rw-tx__points">
                      {s.points.map((p) => <div key={p} className="rw-tx__point"><Tick /><span>{p}</span></div>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rw-tx__thumbs">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show ${s.label}`}
                aria-current={i === idx ? 'true' : undefined}
                className={'rw-tx__thumb' + (i === idx ? ' is-on' : '')}
              >
                <img src={shot(s.id).src} alt="" loading="lazy" />
                <span className="rw-tx__thumb-label"><span className="is-accent">{pad(i + 1)}</span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── day one ──────────────────────────────────────────────── */}
      <section className="rw-tx__section rw-tx__section--bottom">
        <div className="rw-tx__inner rw-tx__dayone">
          <div className="rw-tx__dayone-text">
            <div className="rw-kicker">Day one</div>
            <h2 className="rw-tx__h2 rw-tx__h2--dayone">This is what opens on first start.</h2>
            <p className="rw-tx__p rw-tx__p--dayone">A real screenshot of TerminalX {r.version}: an empty host list, the Helper signed in and ready, and the tool rail down the left. Pick a host or create one, and the Helper on the right reads the console with you.</p>
            <p className="rw-tx__meta rw-tx__meta--wrap">The other images on this page are illustrations drawn with the application's own palette and icons.</p>
          </div>
          <div className="rw-tx__dayone-shot">
            <Shot item={shot('first')} alt={`TerminalX ${r.version} on first start`} onZoom={zoom} />
          </div>
        </div>
      </section>

      {/* ── keyboard ─────────────────────────────────────────────── */}
      <section className="rw-tx__section rw-tx__section--bottom">
        <div className="rw-tx__inner">
          <div className="rw-kicker">Keyboard first</div>
          <h2 className="rw-tx__h2 rw-tx__h2--keys">Hands stay on the keys.</h2>
          <div className="rw-tx__keys">
            {KEYS.map(([keys, what]) => (
              <div key={what} className="rw-tx__key">
                <span className="rw-tx__kbds">{keys.map((k) => <kbd key={k}>{k}</kbd>)}</span>
                <span className="rw-tx__key-what">{what}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── comparison ───────────────────────────────────────────── */}
      <section className="rw-tx__section">
        <div className="rw-tx__inner">
          <div className="rw-kicker">How it compares</div>
          <h2 className="rw-tx__h2 rw-tx__h2--table">A terminal that moves bytes, and an assistant that can see them.</h2>
          <div className="rw-tx__table-wrap">
            <table className="rw-table rw-tx__table">
              <thead>
                <tr><th><span className="rw-tx__sr">Capability</span></th><th>Terminal clients</th><th>AI chat</th><th className="is-accent">TerminalX</th></tr>
              </thead>
              <tbody>
                {COMPARE.map(([what, term, chat, tx]) => (
                  <tr key={what}><td className="rw-tx__td-strong">{what}</td><td>{term}</td><td>{chat}</td><td className="rw-tx__td-strong">{tx}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── install ──────────────────────────────────────────────── */}
      <section className="rw-tx__section rw-tx__section--rule">
        <div className="rw-tx__inner rw-tx__install">
          <div>
            <div className="rw-kicker">Install</div>
            <h2 className="rw-tx__h2">One file, no administrator prompt.</h2>
            <p className="rw-tx__p rw-tx__p--install">
              Installs for the current user, registers in Apps &amp; features, upgrades in place over a
              running copy and uninstalls cleanly. Claude Code and Memora are set up during install
              when they are missing.
            </p>
            <div className="rw-tx__install-actions">
              <DownloadButton>Download TerminalX {version}</DownloadButton>
              <span className="rw-tx__meta">{r.file}</span>
              <span className="rw-tx__meta rw-tx__meta--wrap">The setup is not yet code-signed, so a clean Windows installation may show a SmartScreen notice on first run.</span>
              <Link to={DOWNLOADS_PATH} className="rw-tx__meta">File size, checksum and what's new are on the Downloads page.</Link>
            </div>
          </div>
          <dl className="rw-tx__specs">
            {SPECS.map(([k, v]) => (
              <div key={k} className="rw-tx__spec"><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── questions ────────────────────────────────────────────── */}
      <section className="rw-tx__section rw-tx__section--rule">
        <div className="rw-tx__inner rw-tx__faq-grid">
          <div>
            <div className="rw-kicker">Questions</div>
            <h2 className="rw-tx__h2 rw-tx__h2--faq">Asked before you install.</h2>
          </div>
          <div className="rw-tx__faqs">
            {FAQ.map(([q, a], i) => {
              const open = faqOpen.includes(i);
              return (
                <div key={q} className="rw-tx__faq">
                  <button type="button" onClick={() => toggleFaq(i)} aria-expanded={open} className="rw-tx__faq-q">
                    <span className="rw-tx__faq-q-text">{q}</span>
                    <span aria-hidden="true" className="rw-tx__faq-sign">{open ? '−' : '+'}</span>
                  </button>
                  {open && <p className="rw-tx__faq-a">{a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {lb != null && (
        <Lightbox images={GALLERY} index={lb} onIndex={setLb} onClose={() => setLb(null)} />
      )}
    </main>
  );
}
